import { useNavigate } from 'react-router-dom';
import { PagePath } from '@monorepo/client-types';
import {
  Account,
  AccountStatus,
  PaginationRequest,
  Program,
} from '@monorepo/types';
import { ACCOUNT_QUERY_KEYS } from '@hub/features/account-dashboard/hooks/constants';
import { useStore } from '@hub/helpers/use-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UseAccountsForMenuResult } from '../types';

/**
 * Fetch accounts for menu using the existing account service
 */
const fetchAccountsForMenu = async (accountStore: {
  paginate: (req: PaginationRequest) => Promise<{ results: Account[] }>;
}): Promise<Account[]> => {
  const paginateRequest: PaginationRequest = {
    page: 1,
    limit: 5,
    filters: {
      status: [AccountStatus.Active],
    },
  };

  const response = await accountStore.paginate(paginateRequest);

  // Return the results array from pagination response
  return response.results || [];
};

/**
 * Hook to fetch accounts for menu with React Query
 * Provides loading, error, and data states
 * Filters out the current account from the list
 */
export const useAccountsForMenu = (): UseAccountsForMenuResult => {
  const {
    dataStore: { accountStore },
  } = useStore();

  const { data, isLoading, error, refetch } = useQuery<Account[], Error>({
    queryKey: ['user-accounts-for-menu'],
    queryFn: () => fetchAccountsForMenu(accountStore),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    accounts: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
};

/**
 * Hook for program selector navigation
 */
export const useAccountNavigation = (onClose?: () => void) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const navigateToOverview = () => {
    navigate(PagePath.Overview);
  };

  const navigateToAccountDashboard = (account: Account) => {
    const accountId = account.tsAccountId || account.tsAccount;

    if (accountId) {
      // Invalidate account-related queries for the target account to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ACCOUNT_QUERY_KEYS.DASHBOARD(accountId),
      });
      queryClient.invalidateQueries({
        queryKey: ACCOUNT_QUERY_KEYS.ACCOUNT_BALANCE(accountId),
      });
    }

    navigate(`${PagePath.Overview}/${accountId}`);
    onClose?.(); // Close menu after account selection
  };

  return {
    navigateToOverview,
    navigateToAccountDashboard,
  };
};

/**
 * Hook for program selector state management
 */
export const useAccountSelector = (onClose?: () => void) => {
  const {
    dataStore: { programStore },
  } = useStore();

  const accountsResult = useAccountsForMenu();
  const navigation = useAccountNavigation(onClose);

  const programs = accountsResult.accounts.map((account: Account) =>
    programStore.get(account.programId),
  );

  const getProgramById = (programId: string) => {
    if (!programId || !programs) {
      return undefined;
    }
    return programs.find(
      (program: Program | undefined) => program?._id === programId,
    );
  };

  return {
    ...accountsResult,
    ...navigation,
    programs,
    getProgramById,
  };
};
